import { defineComponent as _defineComponent } from 'vue'
import { computed } from 'vue';
import { storeToRefs } from 'pinia';

import {
	ModuleStreamingChartEdges,
	ObjectType,
	PopularityRankTypes,
	StreamingChartObjectType,
	StreamingChartsTitlesEdge,
} from '@/@types/graphql-types';

import StreamingChartList from '@/features/streaming-charts/components/StreamingChartList.vue';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';

import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import {
	ChartTitle,
	NormalizedChartTitle,
	normalizeChartTitle,
} from '@/features/streaming-charts/streaming-charts-helper';
import { sliceTitles, provideStreamingChartSpinningText } from '@/helpers/streaming-chart-helper';
import { useStreamingCharts } from '@/features/streaming-charts/composables/useStreamingCharts';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';


export default /*#__PURE__*/_defineComponent({
  __name: 'TitleDetailStreamingChart',
  props: {
    titleId: null,
    titleName: null,
    titleObjectType: null,
    streamingChartData: null,
    lastUpdated: { default: '' },
    isSidebar: { type: Boolean, default: false },
    range: { default: 4 }
  },
  setup(__props: any, { expose }) {

const props = __props as { titleId: string, titleName: string, titleObjectType: ObjectType, streamingChartData?: ModuleStreamingChartEdges[], lastUpdated: string | number | Date, isSidebar: boolean, range: number };



const { country } = storeToRefs(useLanguageStore());

const queryEnabled = computed(() => !props.streamingChartData?.length);

const filter = {
	jwEntityID: props.titleId,
	category: PopularityRankTypes.DailyPopularitySameContentType,
	previousTitles: props.range * 2 + 1,
	nextTitles: props.range * 2 + 1,
	objectType: props.titleObjectType as unknown as StreamingChartObjectType,
};

const queryVariables = computed(() => ({
	withOffers: false,
	filter,
	first: (props.range * 2 + 1) * 2 + 1,
	after: '',
	countryStreamingCharts: country.value,
}));

const { result } = useStreamingCharts(queryVariables, queryEnabled);

const queryData = computed(() => result.value?.streamingCharts);

const streamingChartTitles = computed<ChartTitle[]>(() => {
	if (props.streamingChartData?.length) {
		return sliceTitles(props.titleId, props.streamingChartData, props.range);
	}
	return sliceTitles(props.titleId, queryData.value?.edges ?? [], props.range);
});

const streamingChartLastUpdated = computed(() => {
	if (props.streamingChartData?.length) {
		return props.streamingChartData.find(t => t?.jwEntityID === props.titleId)?.rankInfo?.updatedAt || '';
	}
	const title = queryData.value?.edges?.find(t => t?.node?.id === props.titleId) || ({} as StreamingChartsTitlesEdge);
	return title?.streamingChartInfo?.updatedAt;
});

const renderChart = computed(() => {
	if (!streamingChartTitles.value?.length) return false;
	if (props.streamingChartData?.length) {
		const rank = props.streamingChartData[0]?.rankInfo?.rank;
		return !!rank && rank < 10000;
	}
	return true;
});

const titleDetailTitle = computed<ChartTitle | undefined>(() => {
	if (props.streamingChartData?.length) {
		return (streamingChartTitles.value as ModuleStreamingChartEdges[]).find(t => t.jwEntityID === props.titleId);
	}
	return (streamingChartTitles.value as StreamingChartsTitlesEdge[]).find(t => t.node.id === props.titleId);
});

const titleNodes = computed(() => streamingChartTitles.value);

const normalizedChartTitles = computed(() => streamingChartTitles.value.map(normalizeChartTitle));

function trackClick(event: { index: number; title: NormalizedChartTitle }) {
	TrackingHelper.trackEvent('userinteraction', {
		action: 'title_clicked',
		label: `${props.titleId}, ${event.title.id}`,
		property: 'streaming_chart',
		value: event.index,
	});
}

expose({ renderChart });

return { __sfc: true,props, country, queryEnabled, filter, queryVariables, result, queryData, streamingChartTitles, streamingChartLastUpdated, renderChart, titleDetailTitle, titleNodes, normalizedChartTitles, trackClick, StreamingChartList, faInfoCircle, provideStreamingChartSpinningText }
}

})